import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Drawer,
  Divider,
  Chip,
  Autocomplete,
  createFilterOptions,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useCompanyCore } from '../../../../core/modules/company';
import { useMasterCore } from '../../../../core/modules/master';
import { GetContainersParams } from '../../../../core/modules/container/interfaces/get-containers';
import { DESTINY_CONTAINER_TYPES } from '../../../../core/constants/containerTypes';
import { ContainerCategory } from '../../../../core/interfaces/category.interface';
import ContainerCategorySelect from '../../shared/ContainerCategorySelect';
import { useLocationCore } from '../../../../core/modules/location';
import { useWarehouseCore } from '../../../../core/modules/warehouse';

interface AdvancedFiltersProps {
  filters: GetContainersParams;
  onFiltersChange: (filters: GetContainersParams) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
  loading?: boolean;
}

export const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
  loading = false,
}) => {
  const { companyId } = useCompanyCore();
  const { priceRanges, containerTypes, containerCategories, containerRecipientTypes } = useMasterCore();
  const { locations, getLocations } = useLocationCore();
  const { warehouses, getWarehouses } = useWarehouseCore();

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<GetContainersParams>(filters);
  const [locationInputValue, setLocationInputValue] = useState('');

  type ContainerCategoryOption = {
    id: number;
    name: string;
    acronym?: string | null;
    typeId: number;
    typeName: string;
    isAll?: boolean;
  };

  const containerCategoryOptions = useMemo(() => {
    if (!containerCategories) return [];
    const validCategories = containerCategories.filter((category): category is ContainerCategory =>
      (category as ContainerCategory).container_type_id !== undefined
    );

    const mappedCategories = validCategories.map<ContainerCategoryOption>((category) => {
      const typeName = containerTypes?.find((type) => type.id === category.container_type_id)?.name;
      return {
        id: category.id,
        name: category.name,
        acronym: category.acronym || null,
        typeId: category.container_type_id,
        typeName: typeName || 'Sin tipo',
      };
    });
    return [
      {
        id: 0,
        name: 'Todos',
        acronym: null,
        typeId: 0,
        typeName: 'Todos',
        isAll: true,
      },
      ...mappedCategories,
    ];
  }, [containerCategories, containerTypes]);

  const categoryFilterOptions = useMemo(
    () =>
      createFilterOptions<ContainerCategoryOption>({
        stringify: (option) =>
          option.isAll ? 'todos' : `${option.id} ${option.acronym || ''} ${option.name}`,
      }),
    []
  );

  // Sincronizar el estado local con los filtros externos
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (field: keyof GetContainersParams, value: any) => {
    // Si el campo es priceRange y el valor es "sin-rango", mantenerlo como string
    // Si es un número, convertirlo a número
    let processedValue = value;
    if (field === 'priceRange') {
      if (value === 'sin-rango' || value === '') {
        processedValue = value === 'sin-rango' ? 'sin-rango' : undefined;
      } else if (value !== undefined && value !== null) {
        processedValue = Number(value);
      }
    } else if (field === 'recipientType' && (value === '' || value === undefined)) {
      processedValue = undefined;
    }
    
    const newFilters = { ...localFilters, [field]: processedValue };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleApplyFilters = () => {
    onApplyFilters();
  };

  const handleClearFilters = () => {
    const clearedFilters: GetContainersParams = {
      page: 1,
    };
    setLocalFilters(clearedFilters);
    setLocationInputValue('');
    onFiltersChange(clearedFilters);
    onClearFilters();
  };

  const warehouseMap = useMemo(() => {
    const map = new Map<number, string>();
    warehouses?.forEach((warehouse) => {
      map.set(warehouse.id, warehouse.code);
    });
    return map;
  }, [warehouses]);

  const locationOptions = useMemo(() => {
    const baseOptions = (locations || []).map((location) => ({
      ...location,
      label: `${location.code} - ${warehouseMap.get(location.warehouse_id) || 'SIN BODEGA'}`,
    }));

    return [
      {
        id: -1,
        code: 'SIN_UBICACION',
        description: 'Contenedores sin ubicación',
        warehouse_id: 0,
        label: 'Sin ubicación',
      },
      ...baseOptions,
    ];
  }, [locations, warehouseMap]);

  const filteredLocationOptions = useMemo(() => {
    if (!localFilters.warehouse_id) return locationOptions;
    return locationOptions.filter((option) => option.id === -1 || option.warehouse_id === localFilters.warehouse_id);
  }, [locationOptions, localFilters.warehouse_id]);

  const hasActiveFilters = Object.keys(filters).some(
    key => key !== 'page' && filters[key as keyof GetContainersParams] !== undefined
  );

  const activeFiltersCount = Object.keys(filters).filter(
    key => key !== 'page' && filters[key as keyof GetContainersParams] !== undefined
  ).length;

  return (
    <Box>
      {/* Botón de filtros a la derecha */}
      <Button
        variant="outlined"
        startIcon={<FilterIcon />}
        onClick={() => setFiltersOpen(true)}
        disabled={!companyId}
        sx={{
          minWidth: 120,
          height: 48,
          borderRadius: 2,
          borderColor: hasActiveFilters ? 'primary.main' : 'grey.300',
          color: hasActiveFilters ? 'primary.main' : 'text.primary',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'primary.50',
          }
        }}
      >
        Filtros
        {hasActiveFilters && (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              ml: 1
            }}
          />
        )}
      </Button>

      {/* Drawer de filtros */}
      <Drawer
        anchor="right"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400 },
            p: 3,
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" fontWeight="bold">
              Filtros
            </Typography>
            {hasActiveFilters && (
              <Chip
                label={`${activeFiltersCount} activo${activeFiltersCount !== 1 ? 's' : ''}`}
                color="primary"
                size="small"
              />
            )}
          </Box>
          <IconButton onClick={() => setFiltersOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>

          {/* Código de contenedor */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Código de contenedor"
              value={localFilters.code || ''}
              onChange={(e) => handleFilterChange('code', e.target.value || undefined)}
              placeholder="Buscar por código (no exacto)"
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={localFilters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                label="Status"
              >
                <MenuItem value="">Todos</MenuItem>
                {DESTINY_CONTAINER_TYPES.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Tipo de Contenedor */}
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de Contenedor</InputLabel>
              <Select
                value={localFilters.type || ''}
                onChange={(e) => handleFilterChange('type', e.target.value || undefined)}
                label="Tipo de Contenedor"
              >
                <MenuItem value="">Todos</MenuItem>
                {containerTypes?.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Categoría */}
          <Grid item xs={12}>
            <ContainerCategorySelect
              value={localFilters.category || undefined}
              onChange={(newValue: number | undefined | string) => {
                if (typeof newValue === 'number') {
                  handleFilterChange('category', newValue);
                } else {
                  handleFilterChange('category', undefined);
                }
              }}
              categories={containerCategories as any}
              label="Categoría"
              showAllOption={true}
              allOptionLabel="Todos"
              size="small"
            />
          </Grid>

          {/* Tipo de Recipiente */}
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de Recipiente</InputLabel>
              <Select
                value={localFilters.recipientType || ''}
                onChange={(e) => handleFilterChange('recipientType', e.target.value || undefined)}
                label="Tipo de Recipiente"
              >
                <MenuItem value="">Todos</MenuItem>
                {containerRecipientTypes?.map((recipientType) => (
                  <MenuItem key={recipientType.id} value={recipientType.id}>
                    {recipientType.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Rango de precios */}
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Rango de precios</InputLabel>
              <Select
                value={localFilters.priceRange || ''}
                onChange={(e) => handleFilterChange('priceRange', e.target.value || undefined)}
                label="Rango de precios"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="sin-rango">Sin rango</MenuItem>
                {priceRanges?.map((priceRange) => (
                  <MenuItem key={priceRange.id} value={priceRange.id}>
                    {priceRange.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Ubicación */}
          <Grid item xs={12}>
            <Autocomplete
              options={filteredLocationOptions}
              getOptionLabel={(option) => option.label}
              value={filteredLocationOptions.find((option) => option.id === localFilters.location_id) || null}
              inputValue={locationInputValue}
              onInputChange={(_, newInputValue) => {
                setLocationInputValue(newInputValue);
              }}
              onChange={(_, value) => {
                handleFilterChange('location_id', value?.id ?? undefined);
                if (!value) {
                  setLocationInputValue('');
                }
              }}
              open={locationInputValue.length > 0}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Ubicación"
                  placeholder="Buscar ubicación"
                  size="small"
                />
              )}
              noOptionsText="No hay ubicaciones disponibles"
              clearText="Limpiar"
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />
          </Grid>
        </Grid>

        {/* Botones de acción */}
        <Box sx={{ display: 'flex', gap: 2, mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            fullWidth
            startIcon={<ClearIcon />}
            disabled={!hasActiveFilters}
            sx={{ borderRadius: 2 }}
          >
            Limpiar Filtros
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              handleApplyFilters();
              setFiltersOpen(false);
            }}
            fullWidth
            disabled={loading || !companyId}
            sx={{ borderRadius: 2 }}
          >
            Aplicar Filtros
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
};
